/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect } from 'react';
import { Button, Input, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import type { CommentFormData } from './types';

type CommentFormProps = {
  initialData?: CommentFormData | null;
  onSubmit: (data: CommentFormData) => void;
  onCancel?: () => void;
  isSubmitting: boolean;
  isAnswer?: boolean;
};

export const CommentForm: React.FC<CommentFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  isAnswer = false,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [title, setTitle] = useState(initialData?.title || '');
  const [summary, setSummary] = useState(initialData?.summary || '');
  const [description, setDescription] = useState(
    initialData?.description || ''
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setSummary(initialData.summary || '');
      setDescription(initialData.description || '');
    }
  }, [initialData]);

  const validate = (): boolean => {
    const hasTitle = title.trim().length > 0;
    const hasSummary = summary.trim().length > 0;
    const hasDescription = description.trim().length > 0;

    if (!hasTitle && !hasSummary && !hasDescription) {
      setError(
        intl.formatMessage({ id: 'comment-validation-at-least-one-field' })
      );
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit({
      title: title.trim() || undefined,
      summary: summary.trim() || undefined,
      description: description.trim() || undefined,
    });
  };

  return (
    <div css={styles.form}>
      <Typography.Text strong css={styles.formTitle}>
        {intl.formatMessage({
          id: isAnswer ? 'comment-add-answer' : 'comment-add-comment',
        })}
      </Typography.Text>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'comment-title-label' })}
        </Typography.Text>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={intl.formatMessage({ id: 'comment-title-placeholder' })}
          maxLength={100}
          showCount
          disabled={isSubmitting}
        />
      </div>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'comment-summary-label' })}
        </Typography.Text>
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'comment-summary-placeholder',
          })}
          maxLength={150}
          showCount
          disabled={isSubmitting}
        />
      </div>

      <div css={styles.formField}>
        <Typography.Text css={styles.label}>
          {intl.formatMessage({ id: 'comment-description-label' })}
        </Typography.Text>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'comment-description-placeholder',
          })}
          rows={4}
          maxLength={500}
          showCount
          disabled={isSubmitting}
        />
      </div>

      {error && (
        <Typography.Text type="danger" css={styles.errorText}>
          {error}
        </Typography.Text>
      )}

      <div css={styles.formActions}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isSubmitting}>
            {intl.formatMessage({ id: 'comment-cancel-button' })}
          </Button>
        )}
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}>
          {intl.formatMessage({ id: 'comment-submit-button' })}
        </Button>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  form: {
    background: '#fafafa',
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },

  formTitle: {
    display: 'block',
    fontSize: 14,
    marginBottom: 12,
  },

  formField: {
    marginBottom: 12,

    '&:last-of-type': {
      marginBottom: 0,
    },
  },

  label: {
    display: 'block',
    fontWeight: 500,
    fontSize: 13,
    marginBottom: 6,
    color: '#595959',
  },

  errorText: {
    display: 'block',
    fontSize: 12,
    marginTop: 8,
    marginBottom: 8,
  },

  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
});
